import classnames from 'classnames';
import { Content, Navbar } from 'rbx';
import * as React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';

import { QUERY_MAINTENANCE_MODE } from '../../api/GraphQLQueries/Ops';
import IUserModel from '../../models/IUserModel';
import { storage } from '../../utils/storage';
import IsAdminQuery from '../auth/IsAdminQuery';
import FeedBackModal from '../feedback/FeedBackModal';
import SearchInputField from '../search/SearchInputField';
import Icon from '../ui/Icon';
import Popover from '../ui/Popover';
import LogoutContainer from './../../containers/auth/LogoutContainer';
import Routing from './../../Routing';

interface IProps {
    user: IUserModel;
}

const TopMenu: React.FC<IProps> = () => {
    const [showFeedBackModal, setShowFeedBackModal] = React.useState(false);
    const [showSearchInfo, setShowSearchInfo] = React.useState(false);
    const [menuActive, setMenuActive] = React.useState(false);
    const searchInfoButtonRef = React.useRef<HTMLDivElement>(null);

    const { data, loading, error } = useQuery(QUERY_MAINTENANCE_MODE, {
        fetchPolicy: 'network-only',
    });

    React.useEffect(() => {
        if (!loading && !error && data) {
            const isMaintenanceMode = !!(data && data.maintenanceMode === true);
            storage.set('maintenenceMode', `${isMaintenanceMode}`);
        }
    }, [data, loading, error]);

    const toggleMenu = () => {
        setMenuActive(!menuActive);
    };

    const toggleFeedBackButton = () => {
        setShowFeedBackModal(!showFeedBackModal);
        setMenuActive(false);
    };

    const toggleSearchInfo = () => {
        setShowSearchInfo(!showSearchInfo);
    };

    const renderAdminLinks = () => (
        <>
            <div className="navbar-item">
                <Link to={Routing.GROUPS.route}>Groups</Link>
            </div>

            <div className="navbar-item">
                <Link to={Routing.USER_LIST.route}>Users</Link>
            </div>
        </>
    );

    const renderFeedBackLink = () => (
        <>
            <a className="navbar-item" onClick={toggleFeedBackButton}>
                Contact Us <i className="fa fa-envelope" />
            </a>
        </>
    );

    const renderUserIcon = () => <i className="fa fa-user" />;

    const renderAdminIcon = () => <i className="fa fa-user-crown" />;

    const renderSpacyLink = () => (
        <div className="navbar-item">
            <Link to={Routing.SPACY_CONVERTER.route}>Spacy converter</Link>
        </div>
    );

    const renderMaintenanceBanner = () => {
        if (loading || error || !data || !data.maintenanceMode) {
            return null;
        }

        return (
            <strong className="flex-justify-center bg-yellow py-1">
                Application is currently in maintenance (read-only) mode.
            </strong>
        );
    };

    const renderBanner = () => {
        if (process.env.REACT_APP_TARGET_ENV !== 'production') {
            const env: string = process.env.REACT_APP_TARGET_ENV || process.env.REACT_APP_ENV || '';

            const colours: { [key: string]: string } = {
                integration: 'yellow',
                devtest: 'yellow',
                devtest2: 'yellow',
                staging: 'cyan',
            };
            const colourClasses = [
                'px-1',
                'flex-justify-center',
                `bg-${colours[env] || 'green'}`,
            ].join(' ');

            return <small className={colourClasses}>{env}</small>;
        }
    };

    const renderSearchInfo = () => {
        const refElement = searchInfoButtonRef.current;

        return (
            <>
                <div className="search-info-popup" ref={searchInfoButtonRef}>
                    <button className="button is-transparent" onClick={toggleSearchInfo}>
                        <i className="fa fa-info-circle" />
                    </button>
                </div>

                {showSearchInfo && refElement && (
                    <Popover
                        title="Search Tips"
                        className="search-tips"
                        popoverEventTarget={refElement}
                        onClose={toggleSearchInfo}
                    >
                        <Content className="mb-2" style={{ textAlign: 'left' }}>
                            <ul>
                                <li>
                                    When inputting a search string, the asterisk (*) character will
                                    match any words or letters (eg. <code>butter* valve</code>).
                                </li>
                                <li>
                                    To find only exact matches, surround text with quotations (")
                                    (eg. <code>"butterfly valve wafer"</code> or{' '}
                                    <code>"valve" "47343.2"</code>).
                                </li>
                                <IsAdminQuery renderFor="admin">
                                    <li>
                                        Clicking the dropdown (<Icon icon="fa-caret-down" />) will
                                        show other fields which can be searched:
                                        <br />
                                        <strong>Document Title / Filename: </strong>Document title
                                        in documentum or customized title or filename in S3.
                                        <br />
                                    </li>
                                </IsAdminQuery>
                            </ul>
                        </Content>
                    </Popover>
                )}
            </>
        );
    };

    return (
        <>
            {renderMaintenanceBanner()}
            <nav className="navbar" role="navigation" aria-label="main navigation">
                <div className="navbar-brand">
                    <div className="navbar-item logo-link">
                        <Link to={Routing.INDEX.route}>
                            <img src="/linde-logo.jpg" alt="Linde PLANTSERV®" />
                        </Link>
                    </div>
                    <a
                        role="button"
                        className={classnames('navbar-burger', { 'is-active': menuActive })}
                        aria-label="menu"
                        aria-expanded="false"
                        onClick={toggleMenu}
                    >
                        <span aria-hidden="true" />
                        <span aria-hidden="true" />
                        <span aria-hidden="true" />
                    </a>
                </div>

                <div className={classnames('navbar-menu', { 'is-active': menuActive })}>
                    <div className="navbar-start navbar-search">
                        <div className="navbar-item">
                            <SearchInputField />
                            {renderSearchInfo()}
                        </div>
                    </div>
                    <div className="navbar-start">
                        <div className="navbar-item">
                            {renderBanner()}
                        </div>
                    </div>
                    <div className="navbar-end">
                        <div className="navbar-item">
                            <Link to={Routing.PLANTS.route} data-cy="navbar-plants">
                                Plants
                            </Link>
                        </div>

                        <div className="navbar-item">
                            <Link to={Routing.BASKETS.route} data-cy="topMenu-navbar-baskets">
                                Baskets
                            </Link>
                        </div>

                        <IsAdminQuery
                            renderUser={renderFeedBackLink}
                            renderAdmin={renderAdminLinks}
                        />

                        <div className="navbar-item has-dropdown is-hoverable">
                            <a className="navbar-link is-arrowless" data-cy="topMenu-navbar-profile">
                                <div className="user-profile-icon">
                                    <IsAdminQuery
                                        renderUser={renderUserIcon}
                                        renderAdmin={renderAdminIcon}
                                    />
                                </div>
                            </a>
                            <div className="navbar-dropdown is-right is-boxed">
                                <IsAdminQuery renderAdmin={renderSpacyLink} />
                                <div className="navbar-item">
                                    <Link to={Routing.PROFILE.route}>Profile</Link>
                                </div>
                                <div className="navbar-item">
                                    <LogoutContainer />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
            {showFeedBackModal && <FeedBackModal onClose={toggleFeedBackButton} />}
        </>
    );
};

export default TopMenu;
